import { Button, Card, Image, message, Modal, Spin, Upload } from "antd";
import React, { useState } from "react";
import CropImage from "../components/CropImage";
import {
  FileTextOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import useAxios from "../../../hooks/useAxios";
import StatusMessage from "./components/StatusMessage";
import StatusIndicator, {
  VerifiedIndicator,
} from "./components/StatusIndicator";
import { capitalizeSentence } from "../utils/commonFunctions";

const MB = 3;
const ALLOWED_SIGNATURE_TYPES = [
  "image/jpg",
  "image/jpeg",
  "image/png",
];
const ALLOWED_SIGNATURE_EXTENSIONS = ["jpg", "jpeg", "png"];

const getFileExtension = (fileName = "") =>
  fileName.split(".").pop()?.toLowerCase() || "";

const isPdfFile = (file) =>
  file?.type === "application/pdf" ||
  getFileExtension(file?.name || file?.originalname || file?.filename || file?.path) === "pdf";

const isValidSignatureFile = (file, showMessage = true) => {
  const extension = getFileExtension(file?.name || file?.originalname || "");
  const isValidType =
    ALLOWED_SIGNATURE_TYPES.includes(file?.type) ||
    ALLOWED_SIGNATURE_EXTENSIONS.includes(extension);

  if (!isValidType) {
    if (showMessage) {
      message.error("Only JPG, JPEG, and PNG files are allowed.");
    }
    return false;
  }

  if (file?.size > MB * 1024 * 1024) {
    if (showMessage) {
      message.error(`Signature file size must be less than ${MB}MB.`);
    }
    return false;
  }

  return true;
};

const Signature = ({
  application,
  loadApplication,
  url,
  uploadedFile,
  aiSettings,
}) => {
  const ID = application?._id;
  const [load, setLoad] = useState(false);
  const api = useAxios();
  const [file, setFile] = useState(null);
  const [modal, setModal] = useState(false);
  const [aspectRatio, setAspectRatio] = useState(16 / 9);
  const [crop, setCrop] = useState();
  const [imgSrc, setImgSrc] = useState("");
  const [error, setError] = useState("");

  const toggle = () => setModal(!modal);

  const resetCrop = () => {
    setImgSrc("");
    setCrop(undefined);
    setAspectRatio(16 / 9);
    setFile(null);
  };

  function onSelectFile(fileData) {
    const { file: uploadFile, fileList } = fileData;
    const selectedFile = uploadFile?.originFileObj || uploadFile;

    if (selectedFile && fileList.length > 0) {
      if (!isValidSignatureFile(selectedFile, false)) {
        return;
      }



      setCrop(undefined);
      const aspect = 130 / 140;
      setAspectRatio(aspect);
      setFile(selectedFile);
      const reader = new FileReader();
      reader.addEventListener(
        "load",
        () => {
          setImgSrc(reader.result?.toString() || "");
          toggle();
        }
      );
      reader.readAsDataURL(selectedFile);
    }
  }

  const onClickImgHandler = async (file) => {
    setError("");
    const form = new FormData();
    form.append("candidateSignature", file);
    setLoad(true);
    try {
      const result = await api.put(
        `/candidate/candidateSignature/${ID}`,
        form,
        {
          tokenType: "candidate",
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const { response } = result?.data?.OCRDetails;

      const promise = [loadApplication(false, false, "signature")];
      await Promise.all(promise);
      if (aiSettings?.isActive) {
        if ("error" in response) {
          setError(response?.error);
        } else {
          message.success(`Signature uploaded successfully`, 2);
        }
      } else {
        if ("error" in response) {
          setError(response?.error);
        }
        message.success(`Signature uploaded successfully`, 2);
      }
      resetCrop();
    } catch (err) {
      console.log(err?.response?.data?.error?.OCRDetails?.response?.error);
      setError(err?.response?.data?.error?.OCRDetails?.response?.error);
      setLoad(false);
    } finally {
      setLoad(false);
    }
  };

  const uploaded = uploadedFile && uploadedFile?.length !== 0;
  const uploadedSignature = uploadedFile?.[0];
  const uploadedIsPdf = uploaded && isPdfFile(uploadedSignature);

  return (
    <Spin
      style={{ backgroundColor: "rgba(0, 0, 0, 0.47)" }}
      spinning={load}
      tip={
        <div>
          <p className="text-white font-weight-normal">
            Uploading another file won't affect this one.
          </p>
        </div>
      }
      size="large"
    >
      <Card
        className={`mb-4 bg-white`}
        style={{ border: `1px solid ${""}` }}
        title={null}
      >
        <div className="d-flex flex-column px-4" style={{ gap: 10 }}>
          <div className="d-flex justify-content-center">
            <StatusIndicator
              filledStatus={application?.signatureFilledStatus}
              status={application?.candidateSignatureStatus}
            />
          </div>
          <div>
            <h5 className="text-dark text-center font-weight-bold mb-1">
              Signature
            </h5>
            <StatusMessage
              filledStatus={application?.signatureFilledStatus}
              status={application?.candidateSignatureStatus}
              reason={application?.candidateSignatureRejection}
              type={"Signature"}
            />
          </div>
          <div>
            {uploaded && (
              <hr
                style={{ borderTop: "1px solid #D5D5D5" }}
                className="mb-3"
              ></hr>
            )}
            <div className="d-flex align-items-center flex-column gap-1">
              {uploaded && uploadedIsPdf && (
                <div className="d-flex align-items-center flex-column">
                  <FileTextOutlined style={{ fontSize: 42, color: "#6c757d" }} />
                  <Button
                    type="link"
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View uploaded PDF
                  </Button>
                </div>
              )}
              {uploaded && !uploadedIsPdf && (
                <Image
                  style={{ objectFit: "contain" }}
                  src={url}
                  width={200}
                  height={100}
                />
              )}
              {!application?.candidateSignatureStatus ? (
                <Upload
                  listType="picture"
                  maxCount={1}
                  fileList={null}
                  onChange={(value) => onSelectFile(value)}
                  showUploadList={{
                    showRemoveIcon: false,
                  }}
                  beforeUpload={(selectedFile) =>
                    isValidSignatureFile(selectedFile)
                      ? false
                      : Upload.LIST_IGNORE
                  }
                  accept="image/png,image/jpg,image/jpeg,.png,.jpg,.jpeg"
                >
                  <div className="d-flex align-items-center justify-content-between w-100">
                    <Button
                      data-testid="upload-signature"
                      style={{ border: "1px dashed #6c757d" }}
                      icon={<UploadOutlined />}
                    >
                      {uploaded ? "Re Upload Signature" : "Upload Signature"}
                    </Button>
                  </div>
                </Upload>
              ) : (
                <VerifiedIndicator />
              )}
              {error && <p className="text-danger">{capitalizeSentence(error)}</p>}
            </div>
          </div>
        </div>
      </Card>
      <Modal
        title={`Crop Signature`}
        centered
        open={modal}
        zIndex={9}
        onOk={() => toggle()}
        closable={false}
        width={700}
        footer={null}
        className="crop-ant-modal-body"
      >
        <CropImage
          key={file}
          setCrop={setCrop}
          crop={crop}
          imgSrc={imgSrc}
          file={file}
          toggle={toggle}
          aspectRatio={aspectRatio}
          setAspectRatio={setAspectRatio}
          onClickImgHandler={onClickImgHandler}
        />
      </Modal>
    </Spin>
  );
};

export default Signature;
